import { useState } from "react";
import { Link } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fetcher, asList, imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ItemForm } from "@/components/admin/ItemForm";
import { CategoryManager } from "@/components/admin/CategoryManager";
import { QuoteList } from "@/components/admin/QuoteList";
import { AccountSettings } from "@/components/admin/AccountSettings";

const Stat = ({ label, value, id }) => (
  <div className="border border-[#D4AF37]/20 bg-[#13141C] p-6" data-testid={`stat-${id}`}>
    <p className="font-mono-j text-[11px] uppercase tracking-[0.2em] text-[#A09E97]">{label}</p>
    <p className="mt-3 font-display text-5xl text-[#F5F3EF]">{value}</p>
  </div>
);

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const { mutate } = useSWRConfig();
  const { data: catData } = useSWR("/categories", fetcher);
  const { data: itemData } = useSWR("/items", fetcher);
  const cats = asList(catData);
  const items = asList(itemData);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, item: null });

  const refresh = () => mutate((key) => typeof key === "string" && (key.startsWith("/items") || key === "/categories"));
  const catName = (slug) => cats.find((c) => c.slug === slug)?.name || slug;
  const term = q.toLowerCase();
  const shown = items.filter((i) => (filter === "all" || i.category === filter) &&
    (!term || [i.name, i.supplier, i.code].some((v) => (v || "").toLowerCase().includes(term))));
  const suppliers = new Set(items.map((i) => i.supplier).filter(Boolean)).size;

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    await api.delete(`/items/${it.id}`);
    toast.success("Item deleted");
    refresh();
  };

  return (
    <main className="min-h-screen">
      <header className="border-b border-[#D4AF37]/15">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden sm:inline text-[#A09E97]">{user?.email}</span>
            <Link to="/" className="text-[#E5C158]" aria-label="View site" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" /></Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-[#A09E97] hover:text-[#F5F3EF]"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-[1440px] px-5 sm:px-10 py-12">
        <h1 className="font-display text-5xl text-[#F5F3EF]">Catalogue manager</h1>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat id="items" label="Items" value={items.length} />
          <Stat id="categories" label="Categories" value={cats.length} />
          <Stat id="suppliers" label="Suppliers" value={suppliers} />
          <Stat id="photos" label="Photos" value={items.reduce((s, i) => s + asList(i.images).length, 0)} />
        </div>
        <Tabs defaultValue="items" className="mt-12">
          <TabsList className="bg-[#13141C] border border-[#D4AF37]/20 rounded-full p-1 h-auto">
            <TabsTrigger value="items" data-testid="tab-items" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0D0D11] text-[#A09E97]">Items</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0D0D11] text-[#A09E97]">Categories</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0D0D11] text-[#A09E97]">Quotes</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account" className="rounded-full px-6 py-2 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0D0D11] text-[#A09E97]">Account</TabsTrigger>
          </TabsList>
          <TabsContent value="items" className="mt-8">
            <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between mb-6">
              <div className="flex gap-3 flex-wrap">
                <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-item-filter"
                  className="h-11 bg-[#13141C] border border-[#D4AF37]/25 px-4 text-sm text-[#F5F3EF] rounded-full">
                  <option value="all">All categories</option>
                  {cats.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
                </select>
                <label className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A09E97]" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" data-testid="admin-item-search"
                    className="h-11 w-56 rounded-full bg-[#13141C] border border-[#D4AF37]/25 pl-11 pr-4 text-sm text-[#F5F3EF] focus:outline-none focus:border-[#D4AF37]" />
                </label>
              </div>
              <button onClick={() => setForm({ open: true, item: null })} data-testid="add-item-btn"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[#D4AF37] text-[#0D0D11] text-sm font-medium hover:bg-[#E5C158] transition-colors">
                <Plus className="w-4 h-4" /> New item
              </button>
            </div>
            <div className="border border-[#D4AF37]/15 divide-y divide-[#D4AF37]/10" data-testid="admin-items-list">
              {shown.map((it) => (
                <div key={it.id} className="flex items-center gap-4 p-3 hover:bg-[#13141C] transition-colors" data-testid={`admin-item-${it.id}`}>
                  <img src={imgUrl(it.images?.[0])} alt="" className="w-14 h-14 object-cover bg-[#13141C] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F3EF] truncate">{it.name} {it.featured && <span className="ml-2 font-mono-j text-[10px] text-[#E5C158]">FEATURED</span>}</p>
                    <p className="text-xs text-[#A09E97] truncate">{catName(it.category)} · {it.supplier || "—"} · {it.code || "—"}</p>
                  </div>
                  <button onClick={() => setForm({ open: true, item: it })} data-testid={`edit-item-${it.id}`} className="p-2 text-[#E5C158]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(it)} data-testid={`delete-item-${it.id}`} className="p-2 text-[#A09E97] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {shown.length === 0 && <p className="p-10 text-center text-[#A09E97]">No items.</p>}
            </div>
          </TabsContent>
          <TabsContent value="categories" className="mt-8">
            <CategoryManager cats={cats} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="quotes" className="mt-8">
            <QuoteList />
          </TabsContent>
          <TabsContent value="account" className="mt-8">
            <AccountSettings />
          </TabsContent>
        </Tabs>
      </div>
      <ItemForm open={form.open} item={form.item} cats={cats} onClose={() => setForm({ open: false, item: null })} onSaved={refresh} />
    </main>
  );
}
